import { Dialog } from '@headlessui/react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FreeMode, Navigation, Thumbs } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { getDetailImages, getDetailSezRegions } from '@/services/sez-regions'
import { BaseRequest, ListBasicRequest } from '@/types/sez-regions'
import clsxm from '@/utils/clsxm'
import { useAppSelector } from '@/utils/hooks'
import { titleSez } from '@/utils/string'

const HighlightImage: React.FC = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const swiperRef = useRef<SwiperRef>(null)
  const navigationNextRef = useRef(null)
  const navigationPrevRef = useRef(null)
  const detailNextRef = useRef(null)
  const detailPrevRef = useRef(null)

  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [selectedImage, setSelectedImage] = useState(1)
  const [openDialog, setOpenDialog] = useState(false)

  const requestList: ListBasicRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug), skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const request: BaseRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug) },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: sez } = useAppSelector(getDetailSezRegions.select(request))

  const { data: dataImages } = useAppSelector(getDetailImages.select(requestList))

  const imageHighlight = useMemo(() => {
    return dataImages?.data.items.filter((item) => item.isHighlight)
  }, [dataImages?.data.items])

  const handleSlideChange = (swiper: any) => {
    setSelectedImage(swiper.activeIndex)
  }
  const handleSlideClick = (idx: number) => {
    setSelectedImage(idx)
  }
  const handleSlideNext = () => {
    setSelectedImage((prevState) => prevState + 1)
  }
  const handleSlidePrevious = () => {
    setSelectedImage((prevState) => prevState - 1)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setThumbsSwiper(null)
  }

  useEffect(() => {
    swiperRef.current?.swiper.slideTo(selectedImage)
  }, [selectedImage])

  if (imageHighlight && imageHighlight.length >= 3) {
    return (
      <React.Fragment>
        <div className='container relative flex flex-col gap-4'>
          <div className='relative w-full lg:hidden'>
            <Swiper
              ref={swiperRef}
              allowTouchMove={false}
              navigation={{
                nextEl: navigationNextRef.current,
                prevEl: navigationPrevRef.current,
              }}
              modules={[Navigation]}
              onSwiper={handleSlideChange}
              className='swiper-highlight-image'
            >
              {imageHighlight && imageHighlight.length > 0 ? (
                <>
                  {imageHighlight.map((item) => (
                    <SwiperSlide key={item.xid}>
                      <div
                        className='relative h-full w-full lg:pointer-events-none'
                        onClick={() => setOpenDialog(true)}
                      >
                        <div className='absolute inset-0 z-20' />
                        <ImageByWidthDevice
                          images={item.imageFiles}
                          alt='highlight 1'
                          className='h-full w-full rounded-lg object-cover object-center'
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </>
              ) : null}
            </Swiper>

            <div className='absolute top-1/2 z-20 flex w-full -translate-y-1/2 items-center justify-between px-3'>
              <button
                ref={navigationPrevRef}
                id='highlight-image-prev'
                aria-label='Highlight Image Previous Page'
                className='flex h-7 w-7 items-center justify-center rounded-full bg-white disabled:opacity-50'
                onClick={handleSlideNext}
              >
                <span className='kek-chevron-left' />
              </button>

              <div
                className='h-7 w-[calc(100%-56px)]'
                onClick={() => setOpenDialog(true)}
              />

              <button
                ref={navigationNextRef}
                id='highlight-image-next'
                aria-label='Highlight Image Next Page'
                className='flex h-7 w-7 items-center justify-center rounded-full bg-white disabled:opacity-50'
                onClick={handleSlidePrevious}
              >
                <span className='kek-chevron-right' />
              </button>
            </div>
          </div>

          {imageHighlight && imageHighlight.length > 2 && (
            <div className='grid w-full grid-cols-3 gap-2 lg:gap-6'>
              <div className='relative h-full w-full max-w-[736px] lg:col-span-2 lg:h-[414px]'>
                <button
                  type='button'
                  className='absolute inset-0 z-20 lg:hidden'
                  onClick={() => setSelectedImage(0)}
                />
                {imageHighlight[0].imageFiles && (
                  <ImageByWidthDevice
                    images={imageHighlight[0].imageFiles}
                    alt={imageHighlight[0].imageFiles.desktop?.fileName ?? ''}
                    className={clsxm(
                      'h-full w-full rounded-lg object-cover object-center lg:h-[414px] lg:w-[736px]',
                      selectedImage != 0 && 'opacity-50 lg:opacity-100'
                    )}
                  />
                )}
              </div>

              <div className='relative col-span-2 grid h-fit grid-cols-2 gap-2 lg:col-span-1 lg:grid-cols-1 lg:gap-5'>
                <div className='relative h-full w-full max-w-[350px] lg:h-[197px]'>
                  <button
                    type='button'
                    className='absolute inset-0 z-20 lg:hidden'
                    onClick={() => setSelectedImage(1)}
                  />
                  {imageHighlight[1].imageFiles && (
                    <ImageByWidthDevice
                      images={imageHighlight[1].imageFiles ?? ''}
                      alt={imageHighlight[1].imageFiles.desktop.fileName ?? ''}
                      className={clsxm(
                        'h-full w-full rounded-lg object-cover object-center lg:h-[197px] lg:w-[350px]',
                        selectedImage != 1 && 'opacity-50 lg:opacity-100'
                      )}
                    />
                  )}
                </div>

                <div className='relative h-full w-full max-w-[350px] lg:h-[197px]'>
                  <button
                    type='button'
                    className='absolute inset-0 z-20 lg:hidden'
                    onClick={() => setSelectedImage(2)}
                  />
                  {imageHighlight[2].imageFiles && (
                    <ImageByWidthDevice
                      images={imageHighlight[2].imageFiles}
                      alt={imageHighlight[2].imageFiles.desktop.fileName ?? ''}
                      className={clsxm(
                        'h-full w-full rounded-lg object-cover object-center lg:h-[197px] lg:w-[350px]',
                        selectedImage != 2 && 'opacity-50 lg:opacity-100'
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {dataImages && dataImages?.data.items.length > 0 && (
            <div className='hidden lg:absolute lg:bottom-5 lg:right-9 lg:block lg:w-fit'>
              <button
                type='button'
                className='flex w-full items-center justify-center gap-2 rounded-full bg-red px-4 py-2 lg:w-fit lg:bg-white'
                onClick={() => setOpenDialog(true)}
              >
                <span className='text-13 font-bold text-white lg:text-red'>{t('common:viewMorePicture')}</span>
                <span className='kek-arrow-right text-base font-bold text-white lg:text-red' />
              </button>
            </div>
          )}
        </div>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          className='absolute inset-0 z-menu flex h-full w-full items-center justify-center bg-black/80 lg:bg-white'
        >
          <div className='container flex flex-col items-center justify-center'>
            <div className='flex w-full items-center justify-between lg:px-9 xl:px-[95px]'>
              <h5 className='text-xl font-bold text-white lg:text-2xl lg:leading-8 lg:text-black'>
                {titleSez(String(sez?.data.title), String(router.locale))}
              </h5>

              <button
                className='rounded-full bg-red px-4 py-2 text-[13px] font-bold text-white md:px-5 md:py-3 md:text-base'
                onClick={handleCloseDialog}
              >
                {t('common:close')}
              </button>
            </div>

            <div className='swiper-main-container'>
              <div className='relative z-20 h-full w-full lg:px-9 xl:px-[95px]'>
                <Swiper
                  spaceBetween={10}
                  navigation={{
                    nextEl: detailNextRef.current,
                    prevEl: detailPrevRef.current,
                  }}
                  thumbs={{ swiper: thumbsSwiper }}
                  modules={[FreeMode, Navigation, Thumbs]}
                  className='swiper-highlight-image h-full'
                >
                  {dataImages && dataImages?.data.items.length > 0 && (
                    <>
                      {dataImages?.data.items.map((img) => (
                        <SwiperSlide key={img.title.replaceAll(' ', '').toLowerCase()}>
                          <div className='relative h-full w-full'>
                            <div className='absolute inset-0 z-20' />
                            <ImageByWidthDevice
                              images={img.imageFiles}
                              alt='Detail Image'
                              className='h-full w-full rounded-lg object-cover object-center'
                            />
                          </div>
                        </SwiperSlide>
                      ))}
                    </>
                  )}
                </Swiper>
              </div>

              <div className='absolute top-1/2 z-20 flex w-full -translate-y-1/2 items-center justify-between px-3 lg:px-0'>
                <button
                  ref={detailPrevRef}
                  id='detail-image-prev'
                  aria-label='Detail Image Previous Page'
                  className='flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-image-nav disabled:opacity-50 lg:h-15 lg:w-15 lg:disabled:bg-form-disable lg:disabled:opacity-100'
                  onClick={handleSlidePrevious}
                >
                  <span className='kek-chevron-left lg:text-[28px]' />
                </button>

                <button
                  ref={detailNextRef}
                  id='detail-image-next'
                  aria-label='Detail Image Next Page'
                  className='flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-image-nav disabled:opacity-50 lg:h-15 lg:w-15 lg:disabled:bg-form-disable lg:disabled:opacity-100'
                  onClick={handleSlideNext}
                >
                  <span className='kek-chevron-right lg:text-[28px]' />
                </button>
              </div>
            </div>

            <div className='swiper-main-pagination'>
              <Swiper
                slidesPerView={3}
                slidesPerGroup={1}
                spaceBetween={8}
                onSwiper={setThumbsSwiper}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs]}
                breakpoints={{
                  920: {
                    width: 920,
                    slidesPerView: 5,
                    spaceBetween: 16,
                  },
                }}
                className='swiper-detail-image cursor-pointer'
              >
                {dataImages && dataImages?.data.items.length > 0 && (
                  <>
                    {dataImages?.data.items.map((img, idx) => (
                      <SwiperSlide key={img.title.replaceAll(' ', '').toLowerCase()}>
                        <button
                          className='relative h-full w-full'
                          onClick={() => handleSlideClick(idx)}
                        >
                          <div className='detail-image-wrapper absolute inset-0 z-20' />
                          <ImageByWidthDevice
                            images={img.imageFiles}
                            alt='Detail Image'
                            className='h-full w-full rounded-lg object-cover object-center'
                          />
                        </button>
                      </SwiperSlide>
                    ))}
                  </>
                )}
              </Swiper>
              <div className='mt-[11px] text-center font-inter text-sm font-bold'>
                {`${selectedImage + 1}/${dataImages?.data.metadata.total ?? 0}`}
              </div>
            </div>
          </div>
        </Dialog>
      </React.Fragment>
    )
  }
  return null
}

export default HighlightImage
